import type { ReactNode } from "react";

import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

/**
 * One card, N cells, hairline dividers — the PA surfaces' summary numbers.
 *
 * Replaces five separate treatments of the same idea: the dashboard's icon-tiled
 * stat cards, the Clients gradient panel, the Payments grey box, Legal's 140px
 * donut plus metric grid, and Client detail's two half-width cards. Between them
 * they spent up to 210px saying five numbers.
 *
 * Every cell carries a track, including at 0%. The row of tracks is what makes
 * the strip read as one object rather than four unrelated figures — drop the
 * track on the cells that happen to be empty and the strip develops holes.
 *
 * Below the 1240px desktop breakpoint this stays the horizontal snap rail the
 * dashboard and payments already used, so the numbers remain reachable on a
 * phone; at 1240px and up the cells join into the strip proper.
 */

export type FigureTone =
  | "emerald" // on target
  | "blue" // in progress
  | "amber" // needs attention
  | "red" // over, or blocked
  | "money" // a currency total, not a ratio
  | "none"; // nothing to report

export type Figure = {
  label: string;
  value: ReactNode;
  /** Trails the value a size down — `/6`, `.40`, `%`. Lets a ratio read as one
   *  figure instead of two. */
  suffix?: ReactNode;
  /** One clause of context. Never a restatement of the label. */
  note?: ReactNode;
  /** 0–100. Omitted reads as 0, which still draws the empty track. */
  pct?: number;
  tone?: FigureTone;
  /** For the rare figure whose value colour carries meaning of its own. */
  valueClassName?: string;
  /** This cell's own query hasn't answered yet. Cells are fed by independent
   *  queries, so the strip cannot wait for all of them before rendering —
   *  without this a still-loading figure draws a confident "0". */
  pending?: boolean;
};

const FILL: Record<FigureTone, string> = {
  emerald: "bg-emerald-500",
  blue: "bg-blue-500",
  amber: "bg-amber-500",
  red: "bg-red-500",
  money: "bg-[var(--series-1)]",
  none: "bg-slate-300 dark:bg-slate-600",
};

const CELL_SHELL =
  "min-w-[172px] flex-none snap-start rounded-lg border border-border-subtle bg-card px-[18px] pb-3.5 pt-[13px] " +
  // At desktop the cells stop being cards and become divisions of one.
  "min-[1240px]:min-w-0 min-[1240px]:flex-1 min-[1240px]:rounded-none min-[1240px]:border-0 " +
  "min-[1240px]:border-l min-[1240px]:border-border-divider min-[1240px]:first:border-l-0";

const RAIL =
  "noscroll -mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-1 " +
  "min-[1240px]:mx-0 min-[1240px]:gap-0 min-[1240px]:overflow-hidden min-[1240px]:px-0 min-[1240px]:pb-0 " +
  "min-[1240px]:rounded-lg min-[1240px]:border min-[1240px]:border-border-subtle min-[1240px]:bg-card";

export function FiguresStrip({
  figures,
  className,
}: {
  figures: Figure[];
  className?: string;
}) {
  return (
    <div className={cn(RAIL, className)}>
      {figures.map((figure) => (
        <div key={figure.label} className={CELL_SHELL}>
          <p className="mb-[7px] text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">
            {figure.label}
          </p>
          {figure.pending ? (
            // Same box the value occupies, so a cell resolving doesn't shift the
            // strip: 23px line-height at 1.05 plus the note's line.
            <Skeleton className="h-[24px] w-[68px] rounded" />
          ) : (
            <p
              className={cn(
                "text-[23px] font-bold leading-[1.05] tracking-[-0.025em] text-foreground",
                figure.valueClassName,
              )}
            >
              {figure.value}
              {figure.suffix ? (
                <span className="ml-px text-[15px] font-semibold text-slate-300 dark:text-slate-600">
                  {figure.suffix}
                </span>
              ) : null}
            </p>
          )}
          <Track pct={figure.pending ? 0 : figure.pct} tone={figure.tone} />
          {figure.pending ? (
            <Skeleton className="mt-1.5 h-[13px] w-[92px] rounded" />
          ) : figure.note ? (
            <p className="mt-1.5 truncate text-[11.5px] text-muted-foreground">
              {figure.note}
            </p>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function Track({ pct, tone = "none" }: { pct?: number; tone?: FigureTone }) {
  const value = Number.isFinite(pct) ? Math.min(100, Math.max(0, pct as number)) : 0;
  // A real but tiny percentage still has to be visible as a mark, or "1% signed"
  // and "nothing signed" draw the same bar. Zero stays genuinely empty.
  const width = value === 0 ? 0 : Math.max(3, value);
  return (
    <div
      aria-hidden
      className="mt-[9px] h-1 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800"
    >
      <div
        className={cn("h-full rounded-full", FILL[tone])}
        style={{ width: `${width}%` }}
      />
    </div>
  );
}

/** Loading shape: one block the height of the strip, not a stack of pills. */
export function FiguresStripSkeleton({ className }: { className?: string }) {
  return <Skeleton className={cn("h-[100px] w-full rounded-lg", className)} />;
}
